import React, { useEffect, useState } from 'react';
import { FURNITURE, PAGE_PAD } from '../layout';
import { Skeleton } from '../../shared/Skeleton';
import { telHref } from '../../shared/format';
import { initialsOf, friendlyTimezone } from './clientFacts';
import { BlockError } from './states';
import {
  Placeholder, NOT_SET, INDUSTRY_PLACEHOLDER, TIMEZONE_PLACEHOLDER, OWNER_UNKNOWN_TITLE, SUBSCRIPTION_UNKNOWN_TITLE, SUGGESTED_CONTACT_TITLE,
} from './placeholders';

// The client header: identity (initials tile, name, "location · industry · timezone"),
// the actions, and the standing facts as a dense labelled row of SIX columns (primary
// contact · phone · email · services · subscription · account owner). Furniture, not a
// card. The columns are the same for every client, so nothing shifts on data: a field
// with no value shows a neutral placeholder ("-"), never a guessed value.
//
// Two rows so the sticky rule can differ by breakpoint (ClientDetailLayout): the
// name row sticks everywhere; from `lg` the facts row sticks with it.

const TILE = 'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-200 text-lg font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200';
// A logo sits on white in both themes (logos are drawn for light backgrounds); initials keep the grey tile.
const TILE_INITIALS = `${TILE} bg-gray-100 dark:bg-gray-800`;
const TILE_LOGO = `${TILE} overflow-hidden bg-white p-1`;

/**
 * The identity tile: the client's logo when there is one and it loads, the initials otherwise.
 * Display only. The image is decorative (the name sits beside it), and a logo that fails to
 * load falls back to the initials instead of leaving a broken image.
 */
const LogoTile = ({ logoUrl, initials }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [logoUrl]);
  if (logoUrl && !failed) {
    return (
      <div aria-hidden="true" className={TILE_LOGO}>
        <img src={logoUrl} alt="" onError={() => setFailed(true)} className="h-full w-full object-contain" />
      </div>
    );
  }
  return <div aria-hidden="true" className={TILE_INITIALS}>{initials}</div>;
};

const LABEL = 'text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

/** One labelled value; a missing value shows the "-" placeholder. */
const Fact = ({ label, children, title, missingTitle }) => {
  const missing = children == null || children === '' || children === false;
  return (
    <div className="min-w-0">
      <dt className={LABEL}>{label}</dt>
      <dd className="mt-1 truncate text-base font-medium text-gray-900 dark:text-gray-100" title={missing ? undefined : title}>
        {missing ? <Placeholder title={missingTitle}>{NOT_SET}</Placeholder> : children}
      </dd>
    </div>
  );
};

// Phone and email are links but read as plain text: colour is reserved for the one
// primary action, and the underline on hover says they are clickable.
const linkClass = 'rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';

/**
 * The subscription value: green only for a status that says the client is active,
 * plain text for any other real status. Tokens like "past_due" read as words.
 * @param {string} status
 * @returns {React.ReactElement}
 */
const SubscriptionValue = ({ status }) => {
  const text = String(status).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  const active = /^active$/i.test(String(status).trim());
  return <span className={active ? 'text-emerald-600 dark:text-emerald-400' : ''}>{text}</span>;
};

/**
 * @param {object} props
 * @param {object} props.facts                 Output of mergeClientFacts.
 * @param {{name?:string, unassigned?:boolean}|null} [props.owner]
 *   The account owner: a name when the backend supplies one, `unassigned` when the
 *   client is known to have none, null when unknown (the field is then omitted).
 * @param {boolean} [props.loading]            Show skeletons instead of facts.
 * @param {*} [props.contextError]            What fn_client_context threw, when it failed: an inline error with
 *   retry — or, for a permission denial, a muted no-access line instead.
 * @param {() => void} [props.onRetry]
 * @param {React.ReactNode} [props.actions]    Buttons for the action group (Compose, Assign owner).
 * @param {React.ReactNode} [props.back]       The way back to the list: a quiet link at the start of the
 *   action group, immediately BEFORE the Compose button. It sits in the sticky name row.
 */
export default function ClientHeader({
  facts, owner = null, loading = false, contextError = null, onRetry, actions = null, back = null,
}) {
  const name = facts?.name || 'Client';
  const contactName = facts?.contact?.name;
  const tel = telHref(facts?.phone);
  const services = facts?.services || [];
  const timezone = friendlyTimezone(facts?.timezone);
  // Location is real when the reads carry it; industry has no source yet and an unset
  // timezone is likewise a placeholder — fixed "not set" text, dimmer than a real value.
  // A friendly timezone name keeps the raw id as its tooltip.
  const subtitle = [
    facts?.location || null,
    facts?.industry || <Placeholder key="industry" dim title="Industry is not available yet.">{INDUSTRY_PLACEHOLDER}</Placeholder>,
    timezone
      ? <span key="timezone" title={timezone !== facts.timezone ? facts.timezone : undefined}>{timezone}</span>
      : <Placeholder key="timezone" dim title="Timezone is not available yet.">{TIMEZONE_PLACEHOLDER}</Placeholder>,
  ].filter(Boolean);

  return (
    <>
      {/* Name row — sticky at every size; from lg it is static inside the sticky header. */}
      <div
        data-testid="client-header-bar"
        data-sticky
        aria-busy={loading || undefined}
        className={`sticky top-0 z-30 lg:static lg:border-b-0 ${FURNITURE}`}
      >
        <div className={`${PAGE_PAD} flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pb-3 pt-3 lg:pt-4`}>
          {/* basis-48 + flex-1: the name block takes what the buttons leave and truncates,
              instead of its full-length subtitle forcing the buttons onto a second line. */}
          <div className="flex min-w-0 flex-1 basis-48 items-center gap-4">
            <LogoTile logoUrl={loading ? null : facts?.logoUrl} initials={loading ? '' : initialsOf(facts?.name)} />
            <div className="min-w-0">
              {loading ? (
                <div className="space-y-2" aria-hidden="true"><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32" /></div>
              ) : (
                <>
                  <h1 className="truncate text-2xl font-bold text-gray-900 dark:text-gray-100">{name}</h1>
                  <p data-testid="client-subtitle" className="truncate text-sm text-gray-500 dark:text-gray-400">
                    {subtitle.map((part, i) => (
                      <React.Fragment key={i}>{i > 0 ? ' · ' : null}{part}</React.Fragment>
                    ))}
                  </p>
                </>
              )}
            </div>
          </div>
          {/* The buttons (All clients, Compose, Assign owner) take a line of their own, under the name,
              until xl; from xl they sit at the right of the name. min-w-0 + max-w-full (not
              shrink-0) let them wrap among themselves on a phone instead of overflowing the page. */}
          <div data-testid="client-header-actions" className="flex w-full min-w-0 max-w-full flex-wrap items-center gap-2.5 xl:w-auto">{back}{actions}</div>
        </div>
      </div>

      {/* Facts row — from lg it sticks with the name row (its wrapper is sticky). */}
      <div data-testid="client-header-facts" className={FURNITURE}>
        <div className={`${PAGE_PAD} pb-4 pt-1`}>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : (
            <dl data-testid="client-facts" className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-6">
              <Fact label={facts?.contactLabel || 'Contact'} title={facts?.contactSuggested ? SUGGESTED_CONTACT_TITLE : undefined}>{contactName}</Fact>
              <Fact label="Phone">{facts?.phone && (tel ? <a href={tel} className={linkClass}>{facts.phone}</a> : facts.phone)}</Fact>
              <Fact label="Email">{facts?.email && <a href={`mailto:${facts.email}`} className={linkClass}>{facts.email}</a>}</Fact>
              <Fact label="Services" title={services.join(' · ')}>{services.length > 0 ? services.join(' · ') : null}</Fact>
              <Fact label="Subscription" missingTitle={SUBSCRIPTION_UNKNOWN_TITLE}>
                {facts?.subscription ? <SubscriptionValue status={facts.subscription} /> : null}
              </Fact>
              <Fact label="Account owner" missingTitle={OWNER_UNKNOWN_TITLE}>
                {owner?.name
                  ? owner.name
                  : owner?.unassigned
                    ? <span className="text-amber-700 dark:text-amber-300">Unassigned</span>
                    : null}
              </Fact>
            </dl>
          )}
          {contextError && (
            <BlockError
              variant="strip" className="mt-3" error={contextError} what="this client's details"
              message="Couldn't load this client's details." onRetry={onRetry}
            />
          )}
        </div>
      </div>
    </>
  );
}
